import { type ChangeEvent } from 'react'
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import { Column, Row, Stack } from '@shipt/design-system-layouts'
import { Headline, typographyAttributes } from '@shipt/design-system-typography'
import { TextArea, TextInput } from '@shipt/design-system-inputs'
import { Button } from '@shipt/design-system-buttons'
import { Checkbox } from '@shipt/design-system-checkbox'
import { LEGAL_LINKS } from '@/features/shared/constants/legalLinks'
import { calcRem, screenSizes } from '@shipt/design-system-themes'
import { isInvalidGiftCardValue } from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/utils'
import { theme } from '@/features/shared/constants/shiptTheme'

export type GiftCardFormInput = {
  giftImage: {
    src: string
    alt: string
  }
  cardChoice: string
  recipientName: string
  recipientEmail: string
  personalMessage: string
  senderName: string
  senderEmail: string
  termsAndConditions: boolean
}

export const GiftCardRecipientForm = ({
  giftCardForm,
}: {
  giftCardForm: UseFormReturn<GiftCardFormInput, object>
}) => {
  const {
    register,
    control,
    formState: { errors },
  } = giftCardForm
  const cardChoice = useWatch({ control, name: 'cardChoice' })
  const termsAndConditionsWaiver = useWatch({
    control,
    name: 'termsAndConditions',
  })

  const isSubmitDisabled = Boolean(
    !termsAndConditionsWaiver || !isEmpty(errors) || isInvalidGiftCardValue(cardChoice),
  )

  return (
    <Column spacing="sm" padding={{ base: [0, 'lg'], sm: [0] }}>
      <Headline>To</Headline>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={{ md: 'xl' }}>
        <ContactInfo>
          <TextInput
            {...register('recipientName', {
              required: "Please enter recipient's name",
              setValueAs: (value) => value.trim(),
              pattern: {
                value: /[a-zA-Z]+/,
                message: "Please enter recipient's name",
              },
            })}
            name="recipientName"
            isClearable={false}
            placeholder="Recipient name"
            errorMessage={errors.recipientName?.message}
            {...typographyAttributes({ isPII: true })}
          />
        </ContactInfo>
        <ContactInfo>
          <TextInput
            {...register('recipientEmail', {
              required: "Please enter recipient's email address",
            })}
            name="recipientEmail"
            isClearable={false}
            placeholder="Recipient email"
            errorMessage={errors.recipientEmail?.message}
            type="email"
            {...typographyAttributes({ isPII: true })}
          />
        </ContactInfo>
      </Stack>
      <Controller
        control={control}
        name="personalMessage"
        render={({ field: { onChange, value } }) => {
          return (
            <PersonalMessageTextArea
              id="personal-message"
              rows={4}
              maxLength={250}
              placeholder="Personal message (optional)"
              aria-label="Personal message (optional)"
              size="md"
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
              value={value}
              {...typographyAttributes({ isPII: true })}
            />
          )
        }}
      />
      <Headline>From</Headline>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 'xxs', md: 'xl' }}>
        <ContactInfo>
          <TextInput
            {...register('senderName', {
              required: "Please enter sender's name",
              setValueAs: (value) => value.trim(),
              pattern: {
                value: /[a-zA-Z]+/,
                message: "Please enter sender's name",
              },
            })}
            name="senderName"
            isClearable={false}
            placeholder="Sender name"
            errorMessage={errors.senderName?.message}
            {...typographyAttributes({ isPII: true })}
          />
        </ContactInfo>
        <ContactInfo>
          <TextInput
            {...register('senderEmail', {
              required: "Please enter sender's email address",
            })}
            name="senderEmail"
            isClearable={false}
            placeholder="Sender email"
            type="email"
            errorMessage={errors.senderEmail?.message}
            {...typographyAttributes({ isPII: true })}
          />
        </ContactInfo>
      </Stack>
      <Column align="center">
        <Controller
          control={control}
          name="termsAndConditions"
          render={({ field: { onChange, value } }) => {
            return (
              <Row spacing="sm">
                <Checkbox
                  label=""
                  onChange={onChange}
                  checked={Boolean(value)}
                  name="termsAndConditions"
                  id="termsAndConditions"
                  size="sm"
                />
                <Label htmlFor="termsAndConditions">
                  I agree to the Shipt&nbsp;
                  <a href={LEGAL_LINKS.GIFT_CARDS} target="_blank">
                    Gift Card
                  </a>
                  &nbsp;and&nbsp;
                  <a href={LEGAL_LINKS.TERMS_OF_SERVICE} target="_blank">
                    Membership
                  </a>
                  &nbsp;Terms and Conditions.
                </Label>
              </Row>
            )
          }}
        />
      </Column>
      <ButtonWrapper align="center" padding={['xl', 0, 0]}>
        <Button
          fullWidth
          size="md"
          variant="primary"
          concept="accent"
          type="submit"
          disabled={isSubmitDisabled}
        >
          Add payment method
        </Button>
      </ButtonWrapper>
    </Column>
  )
}

const ContactInfo = styled.div`
  @media ${screenSizes.tablet} {
    width: 20rem;
  }
`

const PersonalMessageTextArea = styled(TextArea)`
  margin-bottom: 0;
`

const Label = styled.label`
  font-size: ${calcRem(theme.font.static.label.md.fontSize)}rem;
  color: ${theme.color.content.base.neutral.primary};
`

const ButtonWrapper = styled(Column)`
  @media ${screenSizes.tablet} {
    margin: 0 auto;
    width: 50%;
  }
`
